import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch,useSelector } from "react-redux";

import IconButton from "../../../packages/IconButton";

import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges";

import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import TrainTabs from "../../../components/TrainTabs";

import { DATA_APPEND_SECTION_ID } from "../../../constants/global.constants";

import { CorrectIcon, WrongIcon } from "../../../assets/icons";

import { getDatasetDetails, updatePlantDataTrain } from "../../../utils/Utils";

import { getPlantDetails, getSheetColumns } from "../../../network/cipAPI";

import {
  storeDefaultInformation,
  storeFileId,
  storePlantData,
  storePlantDetails,
  storeTrainChemicalSelection,
  updateSelectedTrain,
} from "../../../Store/TemplateStore/template.action";

import "./AnalyseDataTrainTabs.scss";

const AnalyseDataTrainTabs = ({
  config,
  selectedTrain,
  hasUnsavedChanges,
  trainReadinessMap,
  plantId,
  tempSelectedTrain,
  trainTabActions,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { plantData } = useSelector((state) => state);

  const { setShowDialog, allowActionRef, pendingActionRef } =
    useUnsavedChanges();
  const {
    setIsLoading,
    setFileId,
    setMappedColumn,
    setMappedReference,
    setSelectedTrain,
    setSelectedSheet,
    setSelectedCIPFileName,
    setHasRecurrentCIPs,
    setIsAppendFile,
    setAppendFileList,
    setAppendStatus,
    setNormSelectionType,
    setDatasetDetails,
    setSheetNames,
    setColumnOptions,
    setFileInfo,
    setUploadedFiles,
    setColMappingType,
    setTempSelectedTrain,
  } = trainTabActions;

  const trainOptions = useMemo(() => {
    const trainCount = config?.trains?.length || 0;
    return Array.from({ length: trainCount }, (_, idx) => String(idx + 1));
  }, [config?.trains?.length]);

  const trainTabs = useMemo(
    () =>
      trainOptions.map((train) => ({
        id: `analyse-data-train-tab-${train}`,
        train,
        title: (
          <div className="analyse-data-train-tab-title">
            <span className="analyse-data-train-tab-label">
              {t("ValueStrings.train")} {train}
            </span>
            <IconButton
              tooltip={
                trainReadinessMap && trainReadinessMap[Number(train)]
                  ? t("analyseData.trainConfigurationCompleted")
                  : t("analyseData.trainConfigurationIncomplete")
              }
              className="analyse-data-train-tab-status-icon"
              stopPropagation={false}
            >
              {trainReadinessMap && trainReadinessMap[Number(train)] ? (
                <CorrectIcon />
              ) : (
                <WrongIcon />
              )}
            </IconButton>
          </div>
        ),
      })),
    [t, trainOptions, trainReadinessMap],
  );

  const resetStateForTrainSwitch = () => {
    setFileId(null);
    setMappedColumn([]);
    setMappedReference(null);
  };

  const scrollToAppendSection = () => {
    const appendSectionElement = document.getElementById(
      DATA_APPEND_SECTION_ID,
    );
    if (!appendSectionElement) return;

    appendSectionElement.style.scrollMarginTop = "38px";
    appendSectionElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleAppendFlowCompletion = () => {
    setShowDialog(false);
    scrollToAppendSection();
    setTempSelectedTrain(null);
  };

  const handleTrainOptionChange = async (value) => {
    if (Number(value) === Number(selectedTrain)) {
      if (tempSelectedTrain) {
        handleAppendFlowCompletion();
      }
      return;
    }
    if (hasUnsavedChanges && allowActionRef.current === false) {
      setShowDialog(true);
      pendingActionRef.current = () => handleTrainOptionChange(value);
      return;
    }

    setFileInfo([]);
    setSelectedSheet(null);
    setSelectedCIPFileName(null);
    setHasRecurrentCIPs(false);
    setIsAppendFile(false);
    setAppendFileList([]);

    if (!value) {
      resetStateForTrainSwitch();
      setSelectedTrain(null);
      setSelectedSheet(null);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getPlantDetails(plantId, true, value);
      if (response && response.data) {
        const {
          file,
          parent_file_name,
          plant_file_id,
          default_cip,
          col_mapping,
          sel_train,
          bulk_cip_file_name,
          has_recurrent_cips,
          owner_username,
          name,
          water_type,
          sheet_names,
          ref_condition,
          isAppended,
          append_files,
          norm_selection_type,
          chemical_selection,
          uploaded_files,
          col_mapping_type,
        } = response.data;

        dispatch(
          storePlantData(updatePlantDataTrain(plantData, plantId, sel_train)),
        );

        let file_id, sheet_name, min_date, max_date, uploaded_in_blob;
        if (file) {
          ({ file_id, sheet_name, min_date, max_date, uploaded_in_blob } =
            file);
        }

        setFileId(file_id);
        const fileInfo = {
          file_id,
          sheetName: sheet_name,
          fileName: parent_file_name,
          plant_file_id,
          uploaded_in_blob,
        };

        dispatch(
          storePlantDetails({
            plantName: name,
            water_type,
          }),
        );
        dispatch(storeFileId(fileInfo));
        setFileInfo(fileInfo);
        if (chemical_selection) {
          dispatch(storeTrainChemicalSelection(chemical_selection));
        }

        if (default_cip) {
          dispatch(storeDefaultInformation(default_cip));
        }

        setMappedColumn(col_mapping);
        setSelectedTrain(sel_train);
        setSelectedCIPFileName(bulk_cip_file_name);
        setHasRecurrentCIPs(has_recurrent_cips);
        setDatasetDetails(
          getDatasetDetails({
            owner_username,
            min_date,
            max_date,
          }),
        );

        setIsAppendFile(isAppended);
        setAppendFileList(append_files);
        setAppendStatus("success");
        setNormSelectionType(norm_selection_type);
        dispatch(updateSelectedTrain(sel_train));
        setSheetNames(sheet_names);
        setSelectedSheet(sheet_name);
        setMappedReference(ref_condition?.[0]);
        setUploadedFiles(uploaded_files);
        setColMappingType(col_mapping_type);

        if (file_id && uploaded_in_blob) {
          setColumnOptions([]);
          const columns = await getSheetColumns(file_id, sel_train);
          if (columns && columns.data) {
            setColumnOptions(columns.data?.cols);
          }
        }
      }

      if (tempSelectedTrain) {
        handleAppendFlowCompletion();
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
      resetStateForTrainSwitch();
      setSelectedSheet(null);
      setSelectedCIPFileName(null);
      setHasRecurrentCIPs(false);
      setIsAppendFile(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tempSelectedTrain) {
      handleTrainOptionChange(tempSelectedTrain);
    }
  }, [tempSelectedTrain]);

  return (
    <TrainTabs
      trainTabs={trainTabs}
      selectedTrain={selectedTrain}
      onTrainChange={handleTrainOptionChange}
      className="analyse-data-train-tabs"
      shellClassName="analyse-data-train-tabs-shell"
    />
  );
};

export default AnalyseDataTrainTabs;
